import { AxiosError } from 'axios'

const CHUNK_LOAD_ERROR_MESSAGES = ['Loading chunk', 'Loading CSS chunk', 'Failed to fetch dynamically imported module']
const CHUNK_LOAD_ERROR_TYPE = 'ChunkLoadError'

// Expected WebAuthn/passkey sign-in failures — a wrong or cancelled passkey, or a second prompt
// fired while one is already pending. These are normal user-side outcomes surfaced to the user via
// a toast, not application bugs, so they must not be captured to Sentry. Matched exactly (not by
// substring) so an unrelated error that merely mentions "credential" is still reported.
const EXPECTED_AUTH_ERROR_MESSAGES = [
  'Invalid credential',
  'Failed to sign in with credential',
  'A request is already pending.',
  'OperationError: A request is already pending.',
]

export const isChunkLoadError = (error: unknown) => {
  if (!error) return false
  if (typeof error !== 'object') return false
  if ('name' in error && error?.name === CHUNK_LOAD_ERROR_TYPE) return true
  if ('type' in error && error?.type === CHUNK_LOAD_ERROR_TYPE) return true
  if ('value' in error && typeof error.value === 'string') {
    const { value } = error
    return CHUNK_LOAD_ERROR_MESSAGES.some((message) => value.includes(message))
  }
  if ('message' in error && typeof error.message === 'string') {
    const { message: errorMessage } = error
    return CHUNK_LOAD_ERROR_MESSAGES.some((message) => errorMessage.includes(message))
  }
  return false
}

/*
  The browser raises `NotAllowedError` when a person dismisses the passkey prompt or lets it time
  out. `ox` wraps it in `WebAuthnP256.CredentialCreationFailedError` (or `...RequestFailedError`)
  and wagmi/viem often wrap that again, so it reaches us either as a linked exception in Sentry's
  `exception.values` or as a wrapper Error whose message carries the original text after
  "Details:". That is the person's choice, not a fault of ours.

  Only the cancel and timeout wording counts. The same `NotAllowedError` is raised for real bugs —
  Safari's "User gesture is not detected…" for a prompt started without user activation, "The
  document is not focused." — and every prompt goes through ox, so neither the type nor the wrapper
  tells a cancel apart. The browsers' own strings:
  - Chromium: "The operation either timed out or was not allowed. See: https://www.w3.org/TR/…"
  - WebKit:   "This request has been cancelled by the user." / "Operation timed out."
*/
const WEBAUTHN_CANCEL_TEXT =
  /timed out or was not allowed|cancelled by the user|(?:^|Details:\s*)Operation timed out\.?\s*$/i

/*
  The OS WebAuthn service is unreachable — Windows raises
  `NotSupportedError: Error connecting to Web Authentication service` (DOMException code 9).
  Nothing in the app can fix that, and the person needs to be told in words rather than have an
  error filed per attempt.

  A browser with no usable WebAuthn API at all (an embedded webview, WebAuthn disabled, an emulated
  device) raises `NotSupportedError: The user agent does not support public key credentials.` —
  equally out of our hands, so it is treated the same way.

  Matched on those sentences alone, never on the `NotSupportedError` type: the same type is raised
  when an authenticator rejects the credential parameters we asked for, and that is our bug to fix.
*/
const WEBAUTHN_UNAVAILABLE_TEXT = /Error connecting to Web Authentication service|does not support public key credentials/i

/** Read the text Sentry exposes on an exception value, or an Error's own message. */
const readErrorText = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined
  if ('value' in error && typeof error.value === 'string') return error.value
  if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message
  }
  return undefined
}

/**
 * A passkey prompt the person dismissed or let time out. Matched on the browser's wording, which
 * survives every wrapper, rather than on the type alone.
 */
export const isWebAuthnCancelError = (error: unknown) => {
  const text = readErrorText(error)
  // The wording is the only evidence of a cancel. A NotAllowedError carrying no message could
  // equally be a missing user gesture or an unfocused document, which are ours to fix, so a bare
  // type is not enough to suppress it — better a report we did not need than a bug we never saw.
  return !!text && WEBAUTHN_CANCEL_TEXT.test(text)
}

/**
 * The platform's WebAuthn service is unreachable, or the browser exposes no usable WebAuthn API,
 * so no passkey can be created or used here.
 */
export const isWebAuthnUnavailableError = (error: unknown) => {
  const text = readErrorText(error)
  return !!text && WEBAUTHN_UNAVAILABLE_TEXT.test(text)
}

export const isExpectedAuthError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false
  const value =
    'value' in error && typeof error.value === 'string'
      ? error.value
      : 'message' in error && typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message
        : undefined
  if (!value) return false
  if (EXPECTED_AUTH_ERROR_MESSAGES.includes(value.trim())) return true
  return isWebAuthnCancelError(error) || isWebAuthnUnavailableError(error)
}

/**
 * User-facing copy for a passkey failure, or undefined when the error is not one we can explain —
 * in which case the caller should fall back to its own generic message and still report it.
 */
export const describeWebAuthnError = (error: unknown): string | undefined => {
  if (isWebAuthnCancelError(error)) {
    return 'The passkey prompt was dismissed or timed out. Try again when you are ready.'
  }
  if (isWebAuthnUnavailableError(error)) {
    return 'This device could not reach its passkey service. Try a different browser, or a device with a screen lock or security key set up.'
  }
  return undefined
}

/**
 * Text for a failed passkey sign-in or reconnect: the WebAuthn copy when we can explain it, else
 * the API's own message, else the error's message.
 */
export const describePasskeyConnectError = (error: unknown): string => {
  const webAuthnCopy = describeWebAuthnError(error)
  if (webAuthnCopy) return webAuthnCopy
  if (error instanceof AxiosError) {
    const apiMessage = (error.response?.data as { message?: unknown } | undefined)?.message
    if (typeof apiMessage === 'string' && apiMessage) return apiMessage
  }
  if (error instanceof Error) return error.message
  return 'An unknown error occurred'
}
